/**
 * lnVerifyInputs — what a LexisNexis Run must carry (Bill 2026-09-17).
 *
 *   "Require DOB; warn on phone/email" → "Phone should be required as well.
 *    email warn but don't run emailage"
 *
 * - required (identity.verify.requiredInputs, default "dob,phone"): Run stays
 *   disabled and names what is missing. The server refuses the same verify
 *   (400 VERIFY_INPUT_REQUIRED), so this copy only makes it visible early.
 * - warn (identity.verify.warnInputs, default "email"): the officer confirms
 *   "Run anyway" before the call.
 *
 * Server mirror: PCBJxChangeApi/Services/Identity/LnVerifyInputRules.cs.
 * A missing settings row falls back to the default; an empty value means none.
 */
import { toNamedJsonDate } from '@/utils/jxDates';
import type { RaoPerson } from '../../../types';

export type LnVerifyInputKey =
  | 'dob' | 'phone' | 'email' | 'ssn' | 'street' | 'city' | 'state' | 'zip' | 'firstName' | 'lastName';

const LABELS: Record<LnVerifyInputKey, string> = {
  dob: 'date of birth',
  phone: 'phone',
  email: 'email',
  ssn: 'SSN',
  street: 'street address',
  city: 'city',
  state: 'state',
  zip: 'ZIP',
  firstName: 'first name',
  lastName: 'last name',
};

export interface LnVerifyInputSettings {
  required: LnVerifyInputKey[];
  warn: LnVerifyInputKey[];
}

export const DEFAULT_LN_VERIFY_INPUTS: LnVerifyInputSettings = {
  required: ['dob', 'phone'],
  warn: ['email'],
};

/** Raw setting value: undefined when the row is absent, '' when set to none. */
function readRaw(systemSettings: unknown, key: string): string | undefined {
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row.settingKey === 'string' ? row.settingKey : typeof row.key === 'string' ? row.key : undefined;
      if (k === key) {
        const v = row.settingValue ?? row.value;
        return typeof v === 'string' ? v : '';
      }
    }
    return undefined;
  }
  if (systemSettings && typeof systemSettings === 'object' && key in (systemSettings as Record<string, unknown>)) {
    const v = (systemSettings as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
    if (v && typeof v === 'object') {
      const inner = (v as Record<string, unknown>).settingValue ?? (v as Record<string, unknown>).value;
      return typeof inner === 'string' ? inner : '';
    }
    return '';
  }
  return undefined;
}

function parseKeys(raw: string | undefined, fallback: LnVerifyInputKey[]): LnVerifyInputKey[] {
  if (raw === undefined) return fallback;
  const known = Object.keys(LABELS) as LnVerifyInputKey[];
  const out: LnVerifyInputKey[] = [];
  for (const part of raw.split(',')) {
    const hit = known.find((k) => k.toLowerCase() === part.trim().toLowerCase());
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out;
}

export function readLnVerifyInputSettings(systemSettings: unknown): LnVerifyInputSettings {
  return {
    required: parseKeys(readRaw(systemSettings, 'identity.verify.requiredInputs'), DEFAULT_LN_VERIFY_INPUTS.required),
    warn: parseKeys(readRaw(systemSettings, 'identity.verify.warnInputs'), DEFAULT_LN_VERIFY_INPUTS.warn),
  };
}

const digits = (v: string | undefined): string => (v ?? '').replace(/\D/g, '');

/** Present means usable by LexisNexis: a full date of birth, a ten-digit phone. */
export function lnInputPresent(person: RaoPerson, key: LnVerifyInputKey): boolean {
  switch (key) {
    // Any date the app can read counts (CHG-0107: a scanned DOB like 01-15-1980 showed on the form but
    // failed the old ISO / M/D/YYYY-only test, so the officer typed a score to get past the run).
    case 'dob': return toNamedJsonDate(person.dob ?? '') !== '';
    case 'phone': return digits(person.phoneCell || person.phoneHome || person.phoneBus).length >= 10;
    case 'email': return !!person.email?.trim();
    case 'ssn': return digits(person.taxId).length === 9;
    case 'street': return !!person.addr?.trim();
    case 'city': return !!person.city?.trim();
    case 'state': return !!person.state?.trim();
    case 'zip': return digits(person.zip).length >= 5;
    case 'firstName': return !!person.firstName?.trim();
    case 'lastName': return !!person.lastName?.trim();
    default: return true;
  }
}

export function missingLnVerifyInputs(person: RaoPerson, settings: LnVerifyInputSettings): LnVerifyInputSettings {
  return {
    required: settings.required.filter((k) => !lnInputPresent(person, k)),
    warn: settings.warn.filter((k) => !lnInputPresent(person, k)),
  };
}

/** "date of birth and phone" / "date of birth, phone and email". */
export function joinLnInputLabels(keys: readonly LnVerifyInputKey[]): string {
  const labels = keys.map((k) => LABELS[k]);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
